import type { FetchError } from 'ofetch';
import type { _GettersTree } from 'pinia';

import { defineStore } from 'pinia';

import { ApiMethod, handleApiError } from '~/utils/apiClient';

import type { Order, OrderRequest } from './types';

const STORE_ID = 'order';

interface Actions {
  createOrder: (data: OrderRequest) => Promise<Order | null>;
  fetchOrder: (id: string, locale: Locale) => Promise<Order | null>;
  fetchOrders: (locale: Locale) => Promise<Order[] | null>;
}

interface State {
  order: Order | null;
  orders: Order[] | null;
}

export const useOrderStore = defineStore<typeof STORE_ID, State, _GettersTree<State>, Actions>(STORE_ID, {
  actions: {
    async createOrder(data: OrderRequest) {
      try {
        const response = await this.$api.fetch<Order>(
          ApiMethod.POST,
          '/orders',
          { cart_id: data.cartId, locale: data.locale },
          true,
        );
        if (response?.data) {
          this.order = response.data;
          return response.data;
        }

        return null;
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        return null;
      }
    },
    async fetchOrder(id: string, locale: Locale) {
      try {
        const response = await this.$api.fetch<Order>(ApiMethod.GET, '/orders', { id, locale }, true);

        if (response?.data) {
          this.order = response.data;
          return response.data;
        }

        return null;
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        return null;
      }
    },
    async fetchOrders(locale: Locale) {
      try {
        const response = await this.$api.fetch<Order[]>(ApiMethod.GET, '/orders/list', { locale }, true);

        if (response?.data) {
          this.orders = response.data;
          return response.data;
        }

        return null;
      } catch (error) {
        const fetchError = error as FetchError;
        await handleApiError(fetchError);

        return null;
      }
    },
  },
  state: () => ({ order: null, orders: null }),
});
